import React, { createContext, useMemo, useState } from 'react';
import {
  Box,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Avatar,
  Divider,
  Chip,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  HomeOutlined,
  ReceiptOutlined,
  DescriptionOutlined,
  PersonOutlined,
  LogoutOutlined,
  BluetoothOutlined,
  Menu as MenuIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Logout, reset } from '../../../fitur/AuthSlice';
import ThermalPrinter from '../ThermalPrinter';
import TombolBukaLaci from '../TombolBukaLaci';
import ScreenShareOutlined from '@mui/icons-material/ScreenShareOutlined';
import { bukaLayarPelanggan } from '../../../utils/layarServer';

const drawerWidth = 280;
export const RetailPrinterContext = createContext({
  printerConnected: false,
  printerInstance: null,
  openPrinterSettings: () => {},
});

const LayoutRetail = ({ children }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [printerConnected, setPrinterConnected] = useState(false);
  const [printerInstance, setPrinterInstance] = useState(null);
  const [showPrinterSettings, setShowPrinterSettings] = useState(false);
  const [pesanLaci, setPesanLaci] = useState(null);

  const handleLogout = () => {
    dispatch(reset());
    dispatch(Logout());
    navigate('/');
  };

  const drawerNavItems = [
     { label: 'Home', icon: <HomeOutlined />, path: '/produkpercabang' },
     { label: 'Rekap', icon: <ReceiptOutlined />, path: '/retail' },
     { label: 'Order Detail', icon: <DescriptionOutlined />, path: '/retail/detail' },
     { label: 'Piutang', icon: <ReceiptOutlined />, path: '/retail/piutang' },
     { label: 'Profile', icon: <PersonOutlined />, path: '/retail/profile' },
   ];

  const bs3 = {
    btnPrimary: {
      bgcolor: '#337ab7',
      color: '#fff',
      border: '1px solid #2e6da4',
      borderRadius: '4px',
      px: 2,
      py: 0.8,
      fontSize: '13px',
      fontWeight: 400,
      textTransform: 'none',
      fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
      '&:hover': { bgcolor: '#286090', borderColor: '#204d74' },
    },
    badge: (bg = '#337ab7') => ({
      display: 'inline-block',
      bgcolor: bg,
      color: '#fff',
      fontSize: '11px',
      fontWeight: 700,
      px: 1,
      py: 0.2,
      borderRadius: '10px',
      lineHeight: 1.4,
      fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
    }),
    text: {
      fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
      fontSize: '13px',
      color: '#333',
    },
  };

  const printerContextValue = useMemo(
    () => ({
      printerConnected,
      printerInstance,
      openPrinterSettings: () => setShowPrinterSettings(true),
    }),
    [printerConnected, printerInstance]
  );

  return (
    <RetailPrinterContext.Provider value={printerContextValue}>
      <Box sx={{ fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif' }}>
      {/* Hamburger Drawer */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: { width: drawerWidth, fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif' },
        }}
      >
        {/* Drawer Header */}
        <Box sx={{ bgcolor: '#337ab7', color: '#fff', p: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ width: 44, height: 44, bgcolor: '#fff', color: '#337ab7', fontWeight: 700, fontSize: '16px' }}>
              {user?.nama?.charAt(0) || user?.username?.charAt(0) || 'K'}
            </Avatar>
            <Box>
              <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#fff', fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif' }}>
                {user?.nama || user?.username || 'Kasir'}
              </Typography>
              <Typography sx={{ fontSize: '12px', color: 'rgba(255,255,255,0.8)', fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif' }}>
                {user?.role || 'Staff Kasir'}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Printer Status - Clickable */}
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #eee' }}>
          <Chip
            icon={<BluetoothOutlined sx={{ fontSize: 16 }} />}
            label={printerConnected ? 'Printer Terhubung' : 'Hubungkan Printer'}
            color={printerConnected ? 'success' : 'default'}
            variant="outlined"
            size="small"
            onClick={() => setShowPrinterSettings(true)}
            sx={{
              width: '100%',
              justifyContent: 'flex-start',
              borderRadius: '4px',
              fontSize: '12px',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: printerConnected ? '#e8f5e9' : '#f5f5f5',
              }
            }}
          />
          <Chip
            icon={<ScreenShareOutlined sx={{ fontSize: 16 }} />}
            label="Layar Pelanggan"
            variant="outlined"
            size="small"
            onClick={bukaLayarPelanggan}
            sx={{ width: '100%', justifyContent: 'flex-start', borderRadius: '4px', fontSize: '12px', cursor: 'pointer', mt: 1 }}
          />
          <TombolBukaLaci
            varian="chip"
            printer={printerInstance}
            cabangUuid={user?.cabang?.uuid}
            onPesan={(severity, pesan) => setPesanLaci({ severity, pesan })}
          />
        </Box>

        {/* Navigation Items */}
        <List sx={{ flex: 1, pt: 1 }}>
          {drawerNavItems.map((item, index) => (
            <ListItemButton
              key={item.path || `action-${index}`}
              onClick={() => {
                navigate(item.path);
                setDrawerOpen(false);
              }}
              sx={{
                mx: 1,
                borderRadius: '4px',
                mb: 0.5,
                height: 44,
                '&:hover': { bgcolor: '#e8f4fd' },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: '#337ab7' }}>{item.icon}</ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  fontSize: '13px',
                  fontWeight: 500,
                  fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
                }}
              />
            </ListItemButton>
          ))}

          <Divider sx={{ my: 1.5 }} />

          <ListItemButton
            onClick={handleLogout}
            sx={{
              mx: 1,
              borderRadius: '4px',
              height: 44,
              color: '#d9534f',
              '&:hover': { bgcolor: '#f2dede' },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40, color: '#d9534f' }}><LogoutOutlined /></ListItemIcon>
            <ListItemText
              primary="Logout"
              primaryTypographyProps={{
                fontSize: '13px',
                fontWeight: 500,
                fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
              }}
            />
          </ListItemButton>
        </List>
      </Drawer>

      {/* Top Bar with Hamburger */}
     <Box sx={{
  bgcolor: '#337ab7',
  color: '#fff',
  px: 1.5,
  py: 0.8,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  position: 'fixed',   
  top: 0,
  left: 0,             
  right: 0,           
  width: '100%',       
  zIndex: 1200,      
  boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
}}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            onClick={() => setDrawerOpen(true)}
            sx={{ color: '#fff', p: 0.5 }}
          >
            <MenuIcon sx={{ fontSize: 24 }} />
          </IconButton>
          <Typography sx={{ fontSize: { xs: '13px', md: '15px' }, fontWeight: 700, fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif' }}>
             <Box sx={bs3.badge('#5bc0de')}>
           Hai, {user?.nama || user?.username || 'Kasir'} silahkan transaksi
          </Box>
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
        
        </Box>
      </Box>
<Box sx={{ p: { xs: 2, md: 2 } }}></Box>
      {/* Main Content */}
      <Box sx={{ p: { xs: 1, md: 1.5 }, bgcolor: '#eee', minHeight: 'calc(100vh - 60px)',pt: '10', }}>
        {children}
      </Box>

      <Snackbar open={!!pesanLaci} autoHideDuration={3000} onClose={() => setPesanLaci(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {pesanLaci ? <Alert severity={pesanLaci.severity} onClose={() => setPesanLaci(null)}>{pesanLaci.pesan}</Alert> : <span />}
      </Snackbar>

      {/* Printer Settings Dialog */}
      <ThermalPrinter
        open={showPrinterSettings}
        onClose={() => setShowPrinterSettings(false)}
        onPrinterConnected={(device) => {
          setPrinterInstance(device);
          setPrinterConnected(true);
        }}
        onPrinterDisconnected={() => {
          setPrinterInstance(null);
          setPrinterConnected(false);
        }}
        currentPrinter={printerInstance}
        connectionStatus={printerInstance?.getConnectionStatus?.()}
      />
      </Box>
    </RetailPrinterContext.Provider>
  );
};

export default LayoutRetail;